import { useNavigate } from 'react-router-dom'
import { motion, useReducedMotion } from 'motion/react'
import { Button } from '../components/ui/Button'
import { useSession } from '../apply/session'

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0, 0, 0.2, 1] as [number, number, number, number] },
  },
}

// ---------------------------------------------------------------------------
// Animated checkmark SVG
// ---------------------------------------------------------------------------
// The same filled-circle mark the Verify screen uses to confirm, recoloured to
// brand blue via the info tokens, so the flow confirms the same way twice
// rather than inventing a second gesture for the final step.
function AnimatedCheckmark() {
  const shouldReduceMotion = useReducedMotion()
  return (
    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={
        shouldReduceMotion
          ? { duration: 0 }
          : { type: 'spring', stiffness: 300, damping: 24 }
      }
      style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'var(--color-info-surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="40" height="40" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path
          d="M7 16l6 6 12-12"
          stroke="var(--color-info-text)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// SuccessScreen
// ---------------------------------------------------------------------------
export function SuccessScreen() {
  const navigate = useNavigate()
  const { application } = useSession()
  const reference = application ? `ZOT-${application.id.slice(0, 8).toUpperCase()}` : '—'

  const checkmarkEl = <AnimatedCheckmark />

  const headingEl = (
    <h1
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'var(--text-display-sm)',
        fontWeight: 'var(--weight-semibold)',
        color: 'var(--color-text-primary)',
        textAlign: 'center',
        margin: 0,
      }}
    >
      Your application has been submitted
    </h1>
  )

  const bodyEl = (
    <p
      style={{
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-body-md)',
        color: 'var(--color-text-secondary)',
        textAlign: 'center',
        lineHeight: 'var(--leading-relaxed)',
        margin: 0,
        maxWidth: 440,
      }}
    >
      Thank you for completing your Zota merchant application. Your information has been securely
      received and is now with our team.
    </p>
  )

  const refEl = (
    <div style={{ textAlign: 'center' }}>
      <p
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-label-sm)',
          color: 'var(--color-text-secondary)',
          margin: '0 0 6px 0',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 'var(--weight-semibold)',
        }}
      >
        Reference number
      </p>
      <div
        style={{
          display: 'inline-block',
          background: 'var(--color-action-primary-hover)',
          borderRadius: 'var(--radius-full)',
          padding: '6px 16px',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-action-primary)',
          letterSpacing: '0.05em',
        }}
      >
        {reference}
      </div>
    </div>
  )

  const nextStepsEl = (
    <div
      style={{
        background: 'var(--color-surface-container-lowest)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-ambient)',
        padding: 'var(--spacing-8)',
        width: '100%',
        textAlign: 'left',
      }}
    >
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-4) 0',
        }}
      >
        What happens next
      </h2>
      <ol
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {[
          'Our compliance team will review your application. This typically takes 2–5 business days.',
          'Your Zota account manager will follow up with you by email to discuss next steps.',
          'Technical integration details will be shared with you separately once your application is approved.',
        ].map((step, i) => (
          <li
            key={i}
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              alignItems: 'flex-start',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--leading-normal)',
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: 22,
                height: 22,
                borderRadius: '50%',
                // Flat blue, not the brand gradient: at 22px the gradient has
                // no room to read as one and just makes the three circles look
                // slightly mismatched. Client decision, 11-08 — keep it flat.
                background: 'var(--color-action-primary)',
                color: 'var(--color-text-on-primary)',
                fontWeight: 'var(--weight-semibold)',
                fontSize: 'var(--text-label-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 1,
              }}
            >
              {i + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    </div>
  )

  const supportEl = (
    <a
      href="mailto:support@zota.com"
      style={{
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-label-sm)',
        color: 'var(--color-action-primary)',
        textDecoration: 'none',
        fontWeight: 'var(--weight-medium)',
      }}
    >
      Have a question? Contact our team
    </a>
  )

  const buttonEl = (
    <Button variant="primary" onClick={() => navigate('/applications')} style={{ width: '100%' }}>
      View your submitted application
    </Button>
  )

  const elements = [checkmarkEl, headingEl, bodyEl, refEl, nextStepsEl, supportEl, buttonEl]

  return (
    <div
      style={{
        minHeight: '100svh',
        background: 'var(--color-surface-base)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-6)',
      }}
    >
      <div style={{ maxWidth: 480, width: '100%' }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--spacing-6)',
            textAlign: 'center',
          }}
        >
          {elements.map((el, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {el}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  )
}
